import { ActionTypeDefinition, JsonObject } from '../domain/types';

/**
 * Helpers that turn the *opaque* halves of the configuration payload into
 * something the Action Builder can render.
 *
 * Both `PdgActionsCatalog.actionTypes[].fields` and
 * `PdgConfiguration.actions` are `JSONObject` scalars on the wire — PDGS
 * owns their shape and can add to it without a BFF schema change. Rather
 * than pretend they are typed, everything here narrows defensively: an
 * entry the UI does not understand is skipped or degraded to a text field
 * instead of throwing and taking the form down with it.
 */

export const ACTION_FIELD_KINDS = [
  'string',
  'number',
  'boolean',
  'select',
  'stringArray',
  'numberArray',
  'object',
  'record',
] as const;

export type ActionFieldKind = (typeof ACTION_FIELD_KINDS)[number];

/**
 * Any value a metadata entry can hold. Stored metadata is read back verbatim
 * rather than coerced to the kind its schema declares: coercing would turn a
 * numeric id into a quoted string, or a nested object into `[object Object]`,
 * and either would be written back to PDGS on the next save.
 */
export type ActionFieldValue =
  | string
  | number
  | boolean
  | null
  | ActionFieldValue[]
  | ActionFieldObject;

export interface ActionFieldObject {
  [key: string]: ActionFieldValue;
}

/** A single metadata input, narrowed from a catalog field schema. */
export interface ActionField {
  key: string;
  label: string;
  kind: ActionFieldKind;
  /** Only meaningful for `select` fields; empty otherwise. */
  options: string[];
  /** Only meaningful for `object` fields; empty otherwise. */
  fields: ActionField[];
  /**
   * Seed value for a new action. `undefined` leaves the key out entirely —
   * used for fields whose empty value PDGS would reject (a number, or an
   * object whose members nobody has filled in yet).
   */
  defaultValue: ActionFieldValue | undefined;
  required: boolean;
  /** Bounds for `number` fields, when the catalog declares them. */
  min?: number;
  max?: number;
  /** Whether a `number` field only accepts whole numbers. */
  integer: boolean;
}

/** One row of the Action Builder, before it is serialised back to the wire. */
export interface ConfigurationActionDraft {
  /**
   * Client-only row identity, used as the React key in the Action Builder.
   *
   * Nothing in an action's own data can serve as one: two actions of the
   * same type and scope are legal, so the values are not unique, and the
   * operator can add and remove rows, so the array position is not stable.
   * Keying on the position instead would make React reuse one row's inputs
   * for another's data after a removal. `toWireAction` drops this, so it
   * never reaches PDGS.
   */
  uid: string;
  type: string;
  scope: string;
  metadata: ActionFieldObject;
}

let actionDraftsBuilt = 0;

/** Fresh row identity. Only has to be unique within the page's lifetime. */
const nextActionUid = (): string => {
  actionDraftsBuilt += 1;

  return `action-draft-${actionDraftsBuilt}`;
};

export const isFieldObject = (value: unknown): value is ActionFieldObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const toStringArrayValue = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(toStringValue);
};

const toOptionalNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const toNumberArrayValue = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (entry): entry is number => toOptionalNumber(entry) !== undefined,
  );
};

const toFieldKind = (value: unknown): ActionFieldKind =>
  ACTION_FIELD_KINDS.includes(value as ActionFieldKind)
    ? (value as ActionFieldKind)
    : 'string';

const toDefaultValue = (
  kind: ActionFieldKind,
  raw: unknown,
  options: string[],
): ActionFieldValue | undefined => {
  if (kind === 'record') {
    // Keys are authored per provider and this form cannot edit them, so a
    // new action leaves the map out rather than seeding a value of the
    // wrong shape.
    return undefined;
  }

  if (kind === 'boolean') {
    return raw === true;
  }

  if (kind === 'number') {
    return toOptionalNumber(raw);
  }

  if (kind === 'stringArray') {
    return toStringArrayValue(raw);
  }

  if (kind === 'numberArray') {
    return toNumberArrayValue(raw);
  }

  if (kind === 'select') {
    const value = toStringValue(raw);

    return options.includes(value) ? value : (options[0] ?? '');
  }

  return toStringValue(raw);
};

/**
 * Default for an `object` field: only the members whose schema declares a
 * `defaultValue` of their own (or nested objects that end up with one).
 *
 * The top-level rule of seeding every field with its empty value does not
 * carry over. PDGS validates a nested block as a whole once it is present —
 * a `clientResolution` with blank strategies, or an `expectedBytesPerRow`
 * without both bounds, is rejected — so a block nobody has filled in has to
 * stay absent rather than be seeded with empties.
 */
const toObjectDefault = (
  rawFields: unknown[],
  fields: ActionField[],
): ActionFieldObject | undefined => {
  const explicitKeys = new Set(
    rawFields
      .filter(isFieldObject)
      .filter(raw => raw.defaultValue !== undefined)
      .map(raw => raw.key),
  );

  const defaults = fields.reduce<ActionFieldObject>((value, field) => {
    const seeded =
      field.kind === 'object' || explicitKeys.has(field.key)
        ? field.defaultValue
        : undefined;

    if (seeded !== undefined) {
      value[field.key] = seeded;
    }

    return value;
  }, {});

  return Object.keys(defaults).length > 0 ? defaults : undefined;
};

/**
 * Narrows the catalog's field schemas. Entries without a usable `key` are
 * dropped — there is nothing to store their value under. `object` fields are
 * narrowed recursively, so a nested block renders as its own sub-form.
 */
export const parseActionFields = (fields: unknown[]): ActionField[] =>
  fields.reduce<ActionField[]>((parsed, field) => {
    if (
      !isFieldObject(field) ||
      typeof field.key !== 'string' ||
      field.key.length === 0
    ) {
      return parsed;
    }

    const kind = toFieldKind(field.kind);
    const options = toStringArrayValue(field.options);
    const rawChildren =
      kind === 'object' && Array.isArray(field.fields) ? field.fields : [];
    const children = parseActionFields(rawChildren);
    const min = toOptionalNumber(field.min);
    const max = toOptionalNumber(field.max);

    parsed.push({
      key: field.key,
      label: typeof field.label === 'string' ? field.label : field.key,
      kind,
      options,
      fields: children,
      defaultValue:
        kind === 'object'
          ? toObjectDefault(rawChildren, children)
          : toDefaultValue(kind, field.defaultValue, options),
      required: field.required === true,
      ...(min === undefined ? {} : { min }),
      ...(max === undefined ? {} : { max }),
      integer: field.integer === true,
    });

    return parsed;
  }, []);

export const findActionType = (
  actionTypes: ActionTypeDefinition[],
  type: string,
): ActionTypeDefinition | undefined =>
  actionTypes.find(actionType => actionType.type === type);

export const buildDefaultMetadata = (
  fields: ActionField[],
): ActionFieldObject =>
  fields.reduce<ActionFieldObject>((metadata, field) => {
    if (field.defaultValue !== undefined) {
      metadata[field.key] = field.defaultValue;
    }

    return metadata;
  }, {});

/**
 * Returns `target` with `key` set to `value`, or removed when `value` is
 * `undefined` — how a cleared number input drops out of the payload instead
 * of being stored as an empty string PDGS would reject.
 */
export const setFieldValue = (
  target: ActionFieldObject,
  key: string,
  value: ActionFieldValue | undefined,
): ActionFieldObject => {
  const rest = { ...target };

  delete rest[key];

  return value === undefined ? rest : { ...rest, [key]: value };
};

/**
 * A brand new action for `actionType`, pre-filled with the catalog's
 * defaults. Also used when the type dropdown changes, so the metadata form
 * always matches the selected type's schema.
 */
export const buildActionDraft = (
  actionType: ActionTypeDefinition | undefined,
): ConfigurationActionDraft => {
  if (!actionType) {
    return { uid: nextActionUid(), type: '', scope: '', metadata: {} };
  }

  return {
    uid: nextActionUid(),
    type: actionType.type,
    scope: actionType.scopes[0] ?? '',
    metadata: buildDefaultMetadata(parseActionFields(actionType.fields)),
  };
};

/**
 * Copies a stored value without reinterpreting it. Anything that is not JSON
 * (it never should be, coming off the wire) is dropped.
 */
const toMetadataValue = (value: unknown): ActionFieldValue | undefined => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map(toMetadataValue)
      .filter((entry): entry is ActionFieldValue => entry !== undefined);
  }

  if (isFieldObject(value)) {
    return Object.entries(value).reduce<ActionFieldObject>(
      (copy, [key, entry]) => setFieldValue(copy, key, toMetadataValue(entry)),
      {},
    );
  }

  return undefined;
};

/** Reads a stored action back into an editable draft. */
export const toActionDraft = (action: JsonObject): ConfigurationActionDraft => {
  const metadata = toMetadataValue(action.metadata);

  return {
    uid: nextActionUid(),
    type: toStringValue(action.type),
    scope: toStringValue(action.scope),
    metadata: isFieldObject(metadata) ? metadata : {},
  };
};

export const toActionDrafts = (
  actions: JsonObject[],
): ConfigurationActionDraft[] => actions.map(toActionDraft);

const isEmptyNestedValue = (value: ActionFieldValue): boolean =>
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isFieldObject(value) && Object.keys(value).length === 0);

const findField = (
  fields: ActionField[],
  key: string,
): ActionField | undefined => fields.find(field => field.key === key);

/**
 * Drops the blanks an operator leaves behind inside an `object` field — an
 * untouched Column input, an empty list, a sub-block nobody filled in.
 *
 * Inside an object, PDGS reads a present key as "set": a `static` strategy
 * with `column: ''` alongside it, or an `expectedBytesPerRow: {}`, fails
 * validation. A blank there can only ever mean "not set", so it is left out.
 *
 * Pruning follows the catalog, not the value's shape. Only members the
 * schema describes as editable are pruned; a `record` member, or a key the
 * schema does not know, was never edited here and goes back exactly as it
 * was loaded — even when it happens to be empty.
 */
const pruneObjectField = (
  value: ActionFieldObject,
  fields: ActionField[],
): ActionFieldObject =>
  Object.entries(value).reduce<ActionFieldObject>((pruned, [key, entry]) => {
    const field = findField(fields, key);

    if (!field || field.kind === 'record') {
      return { ...pruned, [key]: entry };
    }

    const next =
      field.kind === 'object' && isFieldObject(entry)
        ? pruneObjectField(entry, field.fields)
        : entry;

    return isEmptyNestedValue(next) ? pruned : { ...pruned, [key]: next };
  }, {});

/**
 * Serialises a draft for PDGS against its action type's field schemas.
 * Drops the client-only `uid` by construction, and prunes blanks inside
 * `object` fields (dropping one left entirely blank). Every other top-level
 * key keeps its value as-is — including its blanks, as it always has.
 */
export const toWireAction = (
  draft: ConfigurationActionDraft,
  fields: ActionField[],
): JsonObject => ({
  type: draft.type,
  // PDGS treats `scope` as optional but rejects an empty string (it is an
  // enum when present), and a stored action without a scope reads back as
  // `''`. Omit it rather than sending a value PDGS refuses.
  ...(draft.scope !== '' && { scope: draft.scope }),
  metadata: Object.entries(draft.metadata).reduce<ActionFieldObject>(
    (metadata, [key, value]) => {
      const field = findField(fields, key);

      if (field?.kind !== 'object' || !isFieldObject(value)) {
        return { ...metadata, [key]: value };
      }

      const pruned = pruneObjectField(value, field.fields);

      return Object.keys(pruned).length > 0
        ? { ...metadata, [key]: pruned }
        : metadata;
    },
    {},
  ),
});

/**
 * Serialises every draft. An action whose type is missing from the catalog
 * has no schema to prune against, so its metadata goes back untouched.
 */
export const toWireActions = (
  drafts: ConfigurationActionDraft[],
  actionTypes: ActionTypeDefinition[],
): JsonObject[] =>
  drafts.map(draft =>
    toWireAction(
      draft,
      parseActionFields(findActionType(actionTypes, draft.type)?.fields ?? []),
    ),
  );

/** `a, b` ⇄ `['a', 'b']` for list fields, which are edited as text. */
export const formatStringList = (values: ActionFieldValue[]): string =>
  values.map(toStringValue).join(', ');

export const parseStringList = (text: string): string[] =>
  text
    .split(',')
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0);

/**
 * `numberArray` counterpart of `parseStringList`. Entries that are not
 * numbers are dropped rather than stored as `NaN`, which JSON would turn
 * into `null`.
 */
export const parseNumberList = (text: string): number[] =>
  parseStringList(text)
    .map(Number)
    .filter(entry => Number.isFinite(entry));
